
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Check, X, GripVertical } from "lucide-react";
import { useColorThemeOptions } from "@/hooks/useColorThemeOptions";
import { generateGradientColors } from "@/utils/colorUtils";

export const ColorThemesManager = () => {
  const { colorThemes, isLoading, createColorTheme, updateColorTheme, deleteColorTheme, reorderColorThemes } = useColorThemeOptions();
  const [newThemeName, setNewThemeName] = useState("");
  const [newThemeColor, setNewThemeColor] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingColor, setEditingColor] = useState("");
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const handleCreate = async () => {
    if (newThemeName.trim()) {
      await createColorTheme.mutateAsync({
        name: newThemeName.trim(),
        color_hex: newThemeColor || undefined
      });
      setNewThemeName("");
      setNewThemeColor("");
    }
  };

  const handleEdit = (id: string, currentName: string, currentColor?: string) => {
    setEditingId(id);
    setEditingName(currentName);
    setEditingColor(currentColor || "");
  };

  const handleSaveEdit = async () => {
    if (editingId && editingName.trim()) {
      await updateColorTheme.mutateAsync({
        id: editingId,
        updates: { 
          name: editingName.trim(),
          color_hex: editingColor || undefined
        }
      });
      setEditingId(null);
      setEditingName("");
      setEditingColor("");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingColor("");
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this color theme?")) {
      await deleteColorTheme.mutateAsync(id);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    await updateColorTheme.mutateAsync({
      id,
      updates: { is_active: !isActive }
    });
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedItem(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = colorThemes.findIndex(item => item.id === draggedItem);
    const targetIndex = colorThemes.findIndex(item => item.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Create new order array
    const newOrder = [...colorThemes];
    const draggedElement = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedElement);

    // Update display_order for affected items
    const updates = newOrder.map((item, index) => ({
      id: item.id,
      display_order: index
    }));

    reorderColorThemes.mutateAsync(updates);
    setDraggedItem(null);
  };

  if (isLoading) {
    return <div>Loading color themes...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Color Themes Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Enter theme name"
              value={newThemeName}
              onChange={(e) => setNewThemeName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Input
              type="color"
              placeholder="#000000"
              value={newThemeColor}
              onChange={(e) => setNewThemeColor(e.target.value)}
              className="w-20"
            />
            <Button onClick={handleCreate} disabled={!newThemeName.trim()}>
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>

          <div className="space-y-2">
            {colorThemes.map((item) => {
              const gradientColors = item.color_hex ? generateGradientColors(item.color_hex) : null;
              
              return (
                <div 
                  key={item.id} 
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-move hover:bg-gray-50 ${
                    draggedItem === item.id ? 'opacity-50' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, item.id)}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-gray-400" />
                    <Badge variant="outline" className="text-xs">
                      #{item.display_order + 1}
                    </Badge>
                    {editingId === item.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="w-48"
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        />
                        <Input
                          type="color"
                          value={editingColor}
                          onChange={(e) => setEditingColor(e.target.value)}
                          className="w-20"
                        />
                      </div>
                    ) : (
                      <>
                        {item.color_hex && (
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded border border-gray-200"
                              style={{ 
                                background: gradientColors 
                                  ? `linear-gradient(45deg, ${gradientColors.from}, ${gradientColors.to})`
                                  : item.color_hex 
                              }}
                              title={`Gradient: ${gradientColors?.from} → ${gradientColors?.to}`}
                            />
                            <div className="text-xs text-gray-500">
                              {gradientColors?.from} → {gradientColors?.to}
                            </div>
                          </div>
                        )}
                        <span className={item.is_active ? "" : "text-gray-500"}>{item.name}</span>
                      </>
                    )}
                    <Badge variant={item.is_active ? "default" : "secondary"}>
                      {item.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    {editingId === item.id ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={handleSaveEdit}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(item.id, item.is_active)}
                        >
                          {item.is_active ? "Deactivate" : "Activate"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item.id, item.name, item.color_hex)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
